/* eslint-disable camelcase */
/* eslint-disable max-lines */
// TODO: file is too long, split
import {
    MOBILE_REGEX,
    TRACKING_AVC_STEP,
    TRACKING_BUSINESS_TRANSACTION_TYPE,
    TRACKING_FLOW_TYPE,
    TRACKING_FORM_NAME,
    TRACKING_FORM_TYPE,
    TRACKING_PAGE_LEVEL_AVC,
    TRACKING_PAGE_TYPE,
    TRACKING_PLATFORM_TYPE_MOBILE,
    TRACKING_PRODUCT_LINE_P2,
    TRACKING_SITE_AREA,
    TRACKING_TRANSFER_POINT_AVAILABLE,
    COOKIE_NAME_MDD_KEKS,
    GELB_GRAU,
    GRUEN,
    GRUEN_GRAU,
    VODAFONE_HOMEPASSED_BHS,
    VODAFONE_HOMEPASSED_BKV,
    TELEKOM_CONNECTED,
    DEUTSCHE_GLASSFASER_CONNECTED,
    OXG_CONNECTED,
    VODAFONE_CONNECTED,
    VODAFONE_PRESALES,
} from '../constants';
import {
    TrackingAVCPageView,
    TrackingGeneralPageView,
    TrackingOrder,
    TrackingProposition,
    TrackingProduct,
    TrackingAVCPageViewPayload,
} from '../interfaces/tracking';
import {
    TRACKING_BUNDLE,
    TRACKING_BTX_BNT,
    TRACKING_TARGET_AUDIENCE_CONSUMER,
    TRACKING_DISCOUNT,
    TRACKING_PLATFORM_TYPE_RESPONSIVE,
    TRACKING_NOT_LOGGED_IN,
    TRACKING_PAGE_TYPE_PRODUCT_DETAIL,
    TRACKING_SERVICE,
    TRACKING_LOGGED_IN,
} from '@vfde-sails/tracking';
import { getCMSConfig } from './cms';
import { CableProviderType, HomesPassedType } from '../enums/product';
import { AccessSupplierType, TrackingAvcStep, TrackingProductType } from '../enums/tracking';
import { formatNumberToDecimalString } from './formatter';
import { AvcResult, JarvisProduct, ProductOption } from '@vfde-sails/avc-core';
import { getCookie } from '@vfde-sails/utils';

let fiberSiteStructure: string[];

const getPlatformType = () => MOBILE_REGEX.test(navigator.userAgent)
    ? TRACKING_PLATFORM_TYPE_MOBILE
    : TRACKING_PLATFORM_TYPE_RESPONSIVE;

const getDiscountByTariff = (tariff: JarvisProduct): TrackingProduct | undefined => {
    const { discount, price } = tariff;

    if (!discount.gross) {
        return;
    }

    return {
        sku: TRACKING_DISCOUNT + ':' + tariff.id,
        name: TRACKING_DISCOUNT + ':' + tariff.id,
        type: TRACKING_DISCOUNT,
        duration: discount.month,
        price_monthly: `-${formatNumberToDecimalString(+price.monthly - +discount.gross)}`,
        proposition: 1,
        units: 1,
    };
};

const getAProduct = (tariff: JarvisProduct, proposition?: number): TrackingProduct => {
    const { once, monthly } = tariff.price;
    const product: TrackingProduct = {
        sku: tariff.id,
        name: tariff.id,
        // Need to replace fibre to fiber,
        // because tracking expects fiber but jarvis gives back fibre -.-
        type: tariff.productType.replace('fibre', 'fiber'),
        duration: +tariff.duration || 24,
        ...proposition && {
            proposition: 1,
            units: 1,
        },
    };

    if ('number' === typeof once) {
        product.price_once = formatNumberToDecimalString(once);
    } else if ('number' === typeof monthly) {
        product.price_monthly = formatNumberToDecimalString(monthly);
    }

    return product;
};

const getAProposition = (tariff: JarvisProduct): TrackingProposition => ({
    id: 1,
    name: tariff.name,
    target_audience: TRACKING_TARGET_AUDIENCE_CONSUMER,
    retention: TRACKING_BTX_BNT,
    // Need to replace fibre to fiber,
    // because tracking expects fiber but jarvis gives back fibre -.-
    product_type: tariff.productType.replace('fibre', 'fiber'),
    product_line: TRACKING_PRODUCT_LINE_P2,
    product_category: TRACKING_BUNDLE,
    units: 1,
});

const getOptionString = (value: number, isDiscount = false) => isDiscount
    ? formatNumberToDecimalString(`-${Math.abs(value)}`)
    : formatNumberToDecimalString(`${Math.abs(value)}`);

const getOptionTrackingProduct = (option: ProductOption, duration: string, isDiscount = false) => {
    const { once, monthly } = option.price;
    const optionItem: TrackingProduct = {
        sku: option.id,
        name: option.id,
        type: isDiscount ? TRACKING_DISCOUNT : TRACKING_SERVICE,
        proposition: 1,
        units: 1,
    };

    if ('number' === typeof once) {
        // Price "Einmalig"
        optionItem.price_once = getOptionString(once, isDiscount);
    } else if ('number' === typeof monthly) {
        if (option.discount) {
            // Stairway price
            optionItem.duration = option.discount.month;
            optionItem.price_monthly = getOptionString(option.discount.gross, isDiscount);
        } else {
            // Monthly price
            optionItem.duration = +duration;
            optionItem.price_monthly = getOptionString(monthly, isDiscount);
        }
    }

    return optionItem;
};

/**
 * Iterate through all the discounts in a tariff, and if there is match
 * with the specified metacode, then returns it (in 'aProduct' format).
 * @param tariff The tariff in which we are looking for the discount
 * @param metacode Search discount with this metacode in the tariff
 * @returns The matching discount in aProduct format for tracking
 */
export const getOptionByMetacode = (
    tariff?: JarvisProduct,
    metacode?: string | RegExp,
    isDiscount = false,
): TrackingProduct | undefined => {
    if (!tariff?.discount || 0 >= tariff.discount.gross || !metacode) { return; }

    const optionArray = [
        ...tariff.options ?? [], // Standard discounts
        ...tariff.specialOffers ?? [], // Voucher codes
    ];
    const option = optionArray.find(item => 'string' === typeof metacode
        ? item.id === metacode
        : metacode.test(item.id)
    );

    if (!option) { return; }

    return getOptionTrackingProduct(option, tariff.duration, isDiscount);
};

export const getDeviceByMetacode = (
    tariff?: JarvisProduct,
    metacode?: string | RegExp,
    isDiscount = false,
): TrackingProduct | undefined => {
    if (!tariff?.discount || 0 >= tariff.discount.gross || !metacode || !tariff.devices) { return; }

    const device = tariff.devices.find(item => 'string' === typeof metacode
        ? item.id === metacode
        : metacode.test(item.id)
    );

    if (!device) { return; }

    return getOptionTrackingProduct(device, tariff.duration, isDiscount);

};

export const getGeneralPageView = (): TrackingGeneralPageView => {
    if (!fiberSiteStructure) {
        fiberSiteStructure = (window as any)?.VF?.siteStructure;
    }

    return {
        login_status: getCookie(COOKIE_NAME_MDD_KEKS) ? TRACKING_LOGGED_IN : TRACKING_NOT_LOGGED_IN,
        page_levels: fiberSiteStructure,
        page_type: TRACKING_PAGE_TYPE_PRODUCT_DETAIL,
        site_area: TRACKING_SITE_AREA,
        business_transaction_type: TRACKING_BUSINESS_TRANSACTION_TYPE,
        line_of_customer: TRACKING_TARGET_AUDIENCE_CONSUMER,
        flow_type: TRACKING_FLOW_TYPE,
        platform_type: getPlatformType(),
    };
};

export const getTrackingOrder = (
    selectedTariff: JarvisProduct,
): TrackingOrder => {
    const {
        stairway = '',
        provisionFee = '',
        installationFee = '',
        provisionFeeDiscount = '',
        routerDiscount = '',
    } = getCMSConfig().visibleDiscountMetacodes;

    return {
        aProducts: [
            getAProduct(selectedTariff, 1),
            getDiscountByTariff(selectedTariff),
            getOptionByMetacode(selectedTariff, RegExp(provisionFee, 'g')),
            getOptionByMetacode(selectedTariff, provisionFeeDiscount, true),
            getOptionByMetacode(selectedTariff, installationFee),
            getOptionByMetacode(selectedTariff, RegExp(stairway, 'g'), true),
            getDeviceByMetacode(selectedTariff, routerDiscount, true),
        ].filter((aProduct): aProduct is TrackingProduct => Boolean(aProduct)),
        aPropositions: [getAProposition(selectedTariff)],
    };
};

export const getAvailabilityFiberProviders = (passedAccessSupplier: string | undefined,
    passedAvailabilityIndicator: string | undefined, homePassed: HomesPassedType | undefined) => {

    if (homePassed === HomesPassedType.BKV) { return [VODAFONE_HOMEPASSED_BKV]; }
    if (homePassed === HomesPassedType.BHS) { return [VODAFONE_HOMEPASSED_BHS]; }

    if (
        (!passedAccessSupplier || passedAccessSupplier === AccessSupplierType.VFDE) &&
        homePassed !== HomesPassedType.BHS &&
        homePassed !== HomesPassedType.BKV &&
        (!passedAvailabilityIndicator || passedAvailabilityIndicator === GELB_GRAU)
    ) {
        return [VODAFONE_PRESALES];
    }

    const availabilityMap: Record<string, Record<string, string[]>> = {
        [AccessSupplierType.VFDE]: {
            [GRUEN]: [VODAFONE_CONNECTED],
            [GRUEN_GRAU]: [VODAFONE_CONNECTED],
        },
        [AccessSupplierType.OXG]: {
            [GRUEN]: [OXG_CONNECTED],
        },
        [AccessSupplierType.DTAG]: {
            [GRUEN]: [TELEKOM_CONNECTED],
        },
        [AccessSupplierType.DGW]: {
            [GRUEN]: [DEUTSCHE_GLASSFASER_CONNECTED],
        },
    };

    return passedAccessSupplier && passedAvailabilityIndicator
        ? availabilityMap[passedAccessSupplier]?.[passedAvailabilityIndicator]
        : undefined;
};

const getTrackingOfferProps = (result?: AvcResult, accessSupplier?: string,
    availabilityIndicator?: string, homePassed?: HomesPassedType) => {
    if (!result) { return; }

    const { selected, fastest, availability, providerType, connectionType, selfInstall } = result;
    if (!selected || !fastest || !availability) { return; }
    const availableTechnologies: string[] = [
        availability.fiber && TrackingProductType.FIBER,
        availability.cable && TrackingProductType[providerType],
        availability.dsl && TrackingProductType.DSL,
    ].filter(Boolean) as string[];
    const payload: TrackingAVCPageViewPayload = {
        selected_product: getAProduct(selected),
        selected_product_available: result.selectedAvailable,
        recommended_products: [fastest.id],
        available_technologies: availableTechnologies,
        available_bandwidth: `${fastest.download} mbit`,
        tv_connection_type: connectionType.toLowerCase(),
        self_installation_available: selfInstall,
        product_footprint: CableProviderType[providerType],
        transfer_point_available: TRACKING_TRANSFER_POINT_AVAILABLE,
    };
    if (availability.fiber || homePassed === HomesPassedType.BHS || homePassed === HomesPassedType.BKV) {
        payload.availability_fiber_providers =
            getAvailabilityFiberProviders(accessSupplier, availabilityIndicator, homePassed);
    }

    return payload;
};

export const getAVCPageView = (
    selectedTariff: JarvisProduct,
    avcResult: AvcResult | undefined,
    step: TrackingAvcStep,
    accessSupplier?: string,
    availabilityIndicator?: string,
    homePassed?: HomesPassedType
): Partial<TrackingAVCPageView> => {
    if (!fiberSiteStructure) {
        fiberSiteStructure = (window as any)?.VF?.siteStructure;
    }

    const stepData = TRACKING_AVC_STEP[step];

    return {
        page_levels: fiberSiteStructure?.concat([TRACKING_PAGE_LEVEL_AVC, stepData.form_step_name]),
        page_type: TRACKING_PAGE_TYPE,
        form_type: TRACKING_FORM_TYPE,
        form_name: TRACKING_FORM_NAME,
        selected_product: getAProduct(selectedTariff),
        ...stepData,
        ...step === TrackingAvcStep.COMPLETE &&
        getTrackingOfferProps(avcResult, accessSupplier, availabilityIndicator, homePassed),
    };
};
